<template>
  <section class="app-table-wrap payment-receipts-table-wrap">
    <div class="app-table-scroll">
      <table class="app-data-table">
        <thead>
          <tr>
            <th class="app-col-index">#</th>
            <th>
              <button
                type="button"
                class="payment-receipts-table-sort-btn"
                :title="labels.sortTitles.date"
                @click="emitSort('date')"
              >
                <span>{{ labels.columns.date }}</span>
                <i class="fa" :class="sortIconClass('date')" aria-hidden="true"></i>
              </button>
            </th>
            <th>
              <button
                type="button"
                class="payment-receipts-table-sort-btn"
                :title="labels.sortTitles.supplier"
                @click="emitSort('supplier')"
              >
                <span>{{ labels.columns.supplier }}</span>
                <i class="fa" :class="sortIconClass('supplier')" aria-hidden="true"></i>
              </button>
            </th>
            <th>{{ labels.columns.folio }}</th>
            <th>{{ labels.columns.paymentAccount }}</th>
            <th class="app-text-right">
              <button
                type="button"
                class="payment-receipts-table-sort-btn payment-receipts-table-sort-btn--right"
                :title="labels.sortTitles.total"
                @click="emitSort('total')"
              >
                <span>{{ labels.columns.total }}</span>
                <i class="fa" :class="sortIconClass('total')" aria-hidden="true"></i>
              </button>
            </th>
            <th>{{ labels.columns.status }}</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="loading">
            <td :colspan="tableColumnCount" class="app-table-empty">{{ loadingText }}</td>
          </tr>

          <tr v-else-if="normalizedRows.length === 0">
            <td :colspan="tableColumnCount" class="app-table-empty">{{ emptyText }}</td>
          </tr>

          <tr v-else v-for="(row, index) in normalizedRows" :key="row.id || index">
            <td class="app-muted">{{ rowNumber(index) }}</td>
            <td>{{ formatDate(row.date) }}</td>
            <td>
              <a
                v-if="isSupplierCounterpart(row)"
                :href="resolveSupplierHref(row)"
                class="expenses-table-link payment-receipts-supplier-text"
                @click="navigateToSupplierDetail(row, $event)"
              >
                {{ resolveCounterpartName(row) }}
              </a>
              <span v-else class="payment-receipts-supplier-text">{{ resolveCounterpartName(row) }}</span>
            </td>
            <td>
              <a
                v-if="row?.id"
                :href="resolveExpenseReceiptHref(row)"
                class="expenses-table-link"
                @click="navigateToExpenseReceiptDetail(row, $event)"
              >
                {{ resolveFolio(row) }}
              </a>
              <span v-else>{{ resolveFolio(row) }}</span>
            </td>
            <td>
              <div v-if="hasBankData(row)" class="payment-receipts-bank-cell">
                <strong v-if="resolveBankName(row) !== ''">{{ resolveBankName(row) }}</strong>
                <small v-if="resolveBankAccountNumber(row) !== ''">{{ resolveBankAccountNumber(row) }}</small>
              </div>
              <span v-else>{{ resolveEmptyValue() }}</span>
            </td>
            <td class="app-text-right app-strong">
              <span>{{ formatAmountWithCurrency(row.total, row.currency) }}</span>
              <small v-if="shouldShowExchangeRate(row)" class="payment-receipts-table-exchange-rate">
                {{ resolveExchangeRateLabel(row) }}
              </small>
            </td>
            <td>
              <span class="app-status-pill" :class="resolveStatusClass(row)">
                {{ resolveStatusLabel(row) }}
              </span>
            </td>
            <td class="app-table-actions-cell">
              <div class="app-table-actions-inline">
                <button
                  type="button"
                  class="app-table-icon-btn app-table-icon-btn--view"
                  :title="labels.actions.view"
                  @click="$emit('view-expense-receipt', row)"
                >
                  <i class="fa fa-eye" aria-hidden="true"></i>
                </button>

                <button
                  v-if="!isStampedExpenseReceipt(row)"
                  type="button"
                  class="app-table-icon-btn payment-receipts-table-icon-btn--pdf"
                  :title="labels.actions.downloadPdf"
                  @click="$emit('download-pdf', row)"
                >
                  <i class="fa fa-file-pdf-o" aria-hidden="true"></i>
                </button>

                <button
                  v-if="isStampedExpenseReceipt(row)"
                  type="button"
                  class="app-table-icon-btn payment-receipts-table-icon-btn--download"
                  :title="labels.actions.download"
                  @click="$emit('download-files', row)"
                >
                  <i class="fa fa-download" aria-hidden="true"></i>
                </button>

                <div v-if="isStampedExpenseReceipt(row)" class="app-table-actions-menu-wrap">
                  <button
                    type="button"
                    class="app-table-icon-btn"
                    :title="labels.actions.more"
                    @click="toggleActionsMenu(row.id, $event)"
                  >
                    <i class="fa fa-ellipsis-h" aria-hidden="true"></i>
                  </button>
                </div>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="normalizedPagination.total_pages > 1" class="app-table-footer">
      <AppPagination
        :current-page="normalizedPagination.current_page"
        :total-pages="normalizedPagination.total_pages"
        @page-change="requestPage"
      />
    </div>

    <teleport to="body">
      <div
        v-if="openRow && isStampedExpenseReceipt(openRow)"
        class="app-table-actions-menu app-table-floating-menu"
        :style="floatingMenuStyle"
      >
        <button type="button" @click="emitAction('download-pdf', openRow)">{{ labels.actions.downloadPdf }}</button>
        <button type="button" @click="emitAction('download-xml', openRow)">{{ labels.actions.downloadXml }}</button>
      </div>
    </teleport>
  </section>
</template>

<script>
import AppPagination from '@/components/common/AppPagination.vue';
import { formatDateShort } from '@/utils/datePresentation';
import { formatAmountWithCurrency, formatExchangeRate } from '@/utils/numberFormat';
import {
  computeFloatingMenuPosition,
  resolveToggledMenuId,
} from '@/composables/shared/useFloatingActionsMenu';
import {
  navigateToRouteDetail,
  resolveRouteHref,
} from '@/composables/shared/useNavigation';
import {
  resolveSortIconClass,
  resolveSortTogglePayload,
} from '@/composables/shared/useSorting';

export default {
  name: 'ExpenseReceiptsTable',
  components: {
    AppPagination,
  },
  props: {
    rows: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    labels: {
      type: Object,
      required: true,
    },
    loadingText: {
      type: String,
      required: true,
    },
    emptyText: {
      type: String,
      required: true,
    },
    pagination: {
      type: Object,
      default: () => ({
        current_page: 1,
        per_page: 20,
        total_pages: 1,
        total_items: 0,
      }),
    },
    sortField: {
      type: String,
      default: '',
    },
    sortDirection: {
      type: String,
      default: 'asc',
    },
    activeStatusTab: {
      type: String,
      default: 'all',
    },
  },
  emits: [
    'view-expense-receipt',
    'download-files',
    'download-xml',
    'download-pdf',
    'page-change',
    'sort-change',
  ],
  data() {
    return {
      openActionsMenuId: null,
      floatingMenuTop: 0,
      floatingMenuLeft: 0,
    };
  },
  computed: {
    normalizedRows() {
      return Array.isArray(this.rows) ? this.rows : [];
    },
    normalizedPagination() {
      const raw = this.pagination || {};

      return {
        current_page: Number(raw.current_page || 1),
        per_page: Number(raw.per_page || 20),
        total_pages: Number(raw.total_pages || 1),
        total_items: Number(raw.total_items || 0),
      };
    },
    openRow() {
      if (!this.openActionsMenuId) {
        return null;
      }

      return this.normalizedRows.find((row) => Number(row?.id || 0) === Number(this.openActionsMenuId)) || null;
    },
    floatingMenuStyle() {
      return {
        position: 'fixed',
        top: `${this.floatingMenuTop}px`,
        left: `${this.floatingMenuLeft}px`,
        zIndex: 1300,
      };
    },
    tableColumnCount() {
      return 8;
    },
  },
  mounted() {
    document.addEventListener('click', this.handleGlobalClick, true);
    window.addEventListener('resize', this.closeActionsMenu);
    window.addEventListener('scroll', this.closeActionsMenu, true);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleGlobalClick, true);
    window.removeEventListener('resize', this.closeActionsMenu);
    window.removeEventListener('scroll', this.closeActionsMenu, true);
  },
  methods: {
    formatAmountWithCurrency,
    formatExchangeRate,
    rowNumber(index) {
      const currentPage = Number(this.normalizedPagination.current_page || 1);
      const perPage = Number(this.normalizedPagination.per_page || 20);

      return ((currentPage - 1) * perPage) + Number(index) + 1;
    },
    formatDate(value) {
      return formatDateShort(value) || this.resolveEmptyValue();
    },
    sortIconClass(field) {
      return resolveSortIconClass(
        {
          field: this.sortField,
          direction: this.sortDirection,
        },
        field,
      );
    },
    emitSort(field) {
      const nextSort = resolveSortTogglePayload(
        {
          field: this.sortField,
          direction: this.sortDirection,
        },
        field,
      );

      if (!nextSort) {
        return;
      }

      this.$emit('sort-change', nextSort);
    },
    requestPage(page) {
      const nextPage = Number(page || 1);
      if (!Number.isFinite(nextPage)) {
        return;
      }

      if (nextPage < 1 || nextPage > this.normalizedPagination.total_pages) {
        return;
      }

      if (nextPage === this.normalizedPagination.current_page) {
        return;
      }

      this.$emit('page-change', nextPage);
    },
    resolveCounterpartName(row) {
      const supplierName = String(row?.supplier_name || row?.company_name || '').trim();
      if (supplierName !== '') {
        return supplierName;
      }

      const collaboratorName = String(
        row?.collaborator_name
          || row?.collaborator_full_name
          || [row?.collaborator_first_name, row?.collaborator_last_name].filter(Boolean).join(' ')
          || ''
      ).trim();

      return collaboratorName || this.resolveEmptyValue();
    },
    isSupplierCounterpart(row) {
      return Number(row?.supplier_id || 0) > 0;
    },
    resolveSupplierHref(row) {
      return resolveRouteHref(this.$router, 'supplierDetail', 'supplierId', row?.supplier_id);
    },
    navigateToSupplierDetail(row, event) {
      navigateToRouteDetail(this.$router, 'supplierDetail', 'supplierId', row?.supplier_id, event);
    },
    resolveExpenseReceiptHref(row) {
      return resolveRouteHref(this.$router, 'expenseReceiptDetail', 'expenseReceiptId', row?.id);
    },
    navigateToExpenseReceiptDetail(row, event) {
      const isModifiedClick = Boolean(
        event
        && (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)
      );
      if (isModifiedClick) {
        return;
      }

      if (event) {
        event.preventDefault();
      }

      this.$emit('view-expense-receipt', row);
    },
    resolveFolio(row) {
      return String(row?.folio || '').trim() || this.resolveEmptyValue();
    },
    resolveBankName(row) {
      const bankName = String(row?.bank_name || row?.payment_account || '').trim();
      if (bankName !== '') {
        return bankName;
      }

      return String(row?.nom_banco_ord_ext || '').trim();
    },
    resolveBankAccountNumber(row) {
      const accountNumber = String(row?.bank_account_number || '').trim();
      return accountNumber;
    },
    hasBankData(row) {
      return this.resolveBankName(row) !== '' || this.resolveBankAccountNumber(row) !== '';
    },
    resolveCurrencyCode(row) {
      return String(row?.currency || 'MXN').trim().toUpperCase() || 'MXN';
    },
    shouldShowExchangeRate(row) {
      const currency = this.resolveCurrencyCode(row);
      if (currency === 'MXN') {
        return false;
      }

      const exchangeRate = Number(row?.exchange_rate);
      return Number.isFinite(exchangeRate) && exchangeRate > 0;
    },
    resolveExchangeRateLabel(row) {
      const prefix = String(this.labels?.labels?.exchangeRatePrefix || 'TC:').trim();
      const rate = this.formatExchangeRate(row?.exchange_rate, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 4,
        invalidValue: '',
      });

      if (rate === '') {
        return '';
      }

      return `${prefix} ${rate}`.trim();
    },
    resolveStatusLabel(row) {
      const key = String(row?.status_key || '').trim().toLowerCase();
      return this.labels?.statusPills?.[key] || row?.status || this.resolveEmptyValue();
    },
    resolveStatusClass(row) {
      const key = String(row?.status_key || '').trim().toLowerCase();

      return {
        'payment-receipts-status-pill--active': key === 'active',
        'payment-receipts-status-pill--canceled': key === 'canceled',
      };
    },
    isStampedExpenseReceipt(row) {
      const stampedFlag = Number(row?.is_stamped || 0) === 1;
      const cfdiUuid = String(row?.payment_cfdi_uuid || '').trim();
      return stampedFlag || cfdiUuid !== '';
    },
    resolveEmptyValue() {
      return String(this.labels?.labels?.emptyValue || '');
    },
    emitAction(actionName, row) {
      this.closeActionsMenu();
      this.$emit(actionName, row);
    },
    toggleActionsMenu(rowId, event) {
      this.openActionsMenuId = resolveToggledMenuId(this.openActionsMenuId, rowId);

      if (this.openActionsMenuId === null) {
        this.closeActionsMenu();
        return;
      }

      const buttonElement = event?.currentTarget || event?.target;
      const position = computeFloatingMenuPosition(buttonElement, 220, 6);
      this.floatingMenuTop = position.top;
      this.floatingMenuLeft = position.left;
    },
    closeActionsMenu() {
      this.openActionsMenuId = null;
    },
    handleGlobalClick(event) {
      if (!this.openActionsMenuId) {
        return;
      }

      const target = event?.target;
      if (!target) {
        return;
      }

      if (target.closest('.app-table-floating-menu') || target.closest('.app-table-actions-menu-wrap')) {
        return;
      }

      this.closeActionsMenu();
    },
  },
};
</script>

<style scoped>
.payment-receipts-table-sort-btn {
  border: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0;
}

.payment-receipts-table-sort-btn--right {
  margin-left: auto;
}

.payment-receipts-table-exchange-rate {
  display: block;
  margin-top: 2px;
  font-size: 11px;
  color: #6b7280;
  font-weight: 600;
}

.payment-receipts-supplier-text {
  font-weight: 700;
}

.payment-receipts-cell-stack {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.payment-receipts-cell-stack small {
  font-size: 11px;
  color: #6b7280;
}

.payment-receipts-relations-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: flex-end;
}

.payment-receipts-relation-line {
  display: inline-grid;
  grid-template-columns: minmax(0, auto) auto;
  align-items: baseline;
  justify-content: end;
  gap: 8px;
  max-width: 100%;
}

.payment-receipts-relation-amount {
  text-align: right;
}

.amount-cell-tax-line {
  white-space: nowrap;
  font-size: 11px;
  font-weight: 500;
  line-height: 1.2;
  color: #64748b;
}

.payment-receipts-table-icon-btn--download {
  color: inherit;
}

.payment-receipts-table-icon-btn--pdf {
  color: inherit;
}

.payment-receipts-table-icon-btn--pdf i {
  color: #c2410c;
}

.payment-receipts-table-icon-btn--pdf:hover,
.payment-receipts-table-icon-btn--pdf:focus {
  color: inherit;
}

.payment-receipts-table-icon-btn--pdf:hover i,
.payment-receipts-table-icon-btn--pdf:focus i {
  color: #9a3412;
}

.payment-receipts-table-icon-btn--download i {
  color: #b45309;
}

.payment-receipts-table-icon-btn--download:hover,
.payment-receipts-table-icon-btn--download:focus {
  color: inherit;
}

.payment-receipts-table-icon-btn--download:hover i,
.payment-receipts-table-icon-btn--download:focus i {
  color: #92400e;
}

.payment-receipts-bank-cell {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.payment-receipts-bank-cell small {
  font-size: 11px;
  color: #748095;
}

.payment-receipts-status-pill--active {
  color: #1c9b43;
  background: rgba(59, 212, 46, 0.14);
  border: 1px solid rgba(59, 212, 46, 0.3);
}

.payment-receipts-status-pill--canceled {
  color: #b42318;
  background: #fff0f0;
  border: 1px solid rgba(197, 58, 58, 0.3);
}
</style>
